import { useMemo, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Loader2, X } from 'lucide-react';
import { Checkbox } from '../../components/ui/Checkbox';
import { extractErrorMessage } from '../../api/errors';
import { useSubscriptionPackages } from '../../queries/subscriptionPackages';
import { useCreateUserSubscription } from '../../queries/userSubscriptions';
import { UserSummary, useUsers } from '../../queries/users';

interface CreateSubscriptionDialogProps {
  onClose: () => void;
}

const todayDateString = () => new Date().toISOString().split('T')[0];

const userLabel = (u: UserSummary) => [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || `User ${u.id}`;

export function CreateSubscriptionDialog({ onClose }: CreateSubscriptionDialogProps) {
  const { data: packages = [] } = useSubscriptionPackages();
  const activePackages = packages.filter((p) => p.is_active);
  const { data: users = [] } = useUsers();
  const create = useCreateUserSubscription();

  const [userSearch, setUserSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState<UserSummary | null>(null);
  const [packageCode, setPackageCode] = useState('');
  const [billingCycle, setBillingCycle] = useState<'weekly' | 'monthly'>('weekly');
  const [startDate, setStartDate] = useState(todayDateString());
  const [endDate, setEndDate] = useState('');
  const [autoRenew, setAutoRenew] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const selectedPackage = activePackages.find((p) => p.package_code === packageCode);
  // Billing cycle only applies to recurring packages; one-time grants derive
  // their end date from the package's access-duration instead.
  const isOneTime = selectedPackage?.billing_model === 'one_time';

  // Pick a sensible default cycle from the package's access window when one is
  // chosen (<=14 days → weekly, else monthly), leaving the user free to change it.
  const onPackageChange = (code: string) => {
    setPackageCode(code);
    const pkg = activePackages.find((p) => p.package_code === code);
    if (pkg?.access_duration_days != null) {
      setBillingCycle(pkg.access_duration_days <= 14 ? 'weekly' : 'monthly');
    }
  };

  // Names/emails come from the (Cognito-enriched) users list, matching how the
  // Users page searches - filter client-side and resolve to the numeric user id.
  const matches = useMemo(() => {
    const term = userSearch.trim().toLowerCase();
    if (term.length < 2 || selectedUser) return [];
    return users
      .filter((u) => userLabel(u).toLowerCase().includes(term) || (u.email ?? '').toLowerCase().includes(term))
      .slice(0, 8);
  }, [userSearch, users, selectedUser]);

  const handleConfirm = () => {
    if (!selectedUser || !packageCode || !startDate) {
      setError('User, package and start date are required.');
      return;
    }
    if (endDate && endDate < startDate) {
      setError('End date cannot be before the start date.');
      return;
    }
    setError(null);
    create.mutate(
      {
        user_id: selectedUser.id,
        package_code: packageCode,
        start_date: startDate,
        end_date: endDate || undefined,
        auto_renew: autoRenew,
        // One-time packages ignore the cycle server-side, so only send it for recurring ones.
        billing_cycle: isOneTime ? undefined : billingCycle,
      },
      { onSuccess: onClose, onError: (err) => setError(extractErrorMessage(err, 'Failed to create subscription.')) },
    );
  };

  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border border-border bg-background p-6 shadow-lg">
          <Dialog.Title className="text-lg font-semibold">Create manual subscription</Dialog.Title>
          <p className="mt-1 text-sm text-muted-foreground">
            Grants access directly — no Stripe charge. For comp accounts, promos, or manual fixes.
          </p>

          <div className="mt-4 space-y-3">
            <div className="block text-sm">
              User
              {selectedUser ? (
                <div className="mt-1 flex items-center justify-between rounded-md border border-input px-2 py-2">
                  <span className="text-sm">
                    {userLabel(selectedUser)}
                    {selectedUser.email && <span className="text-muted-foreground"> ({selectedUser.email})</span>}
                  </span>
                  <button type="button" onClick={() => setSelectedUser(null)} className="text-muted-foreground hover:text-red-500">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <input
                    className="mt-1 w-full rounded-md border border-input bg-background px-2 py-2 text-sm"
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    placeholder="Search by name or email…"
                  />
                  {matches.length > 0 && (
                    <div className="absolute z-10 mt-1 w-full rounded-md border border-border bg-background shadow-lg">
                      {matches.map((u) => (
                        <button
                          key={u.id}
                          type="button"
                          onClick={() => {
                            setSelectedUser(u);
                            setUserSearch('');
                          }}
                          className="block w-full px-3 py-1.5 text-left text-sm hover:bg-accent"
                        >
                          {userLabel(u)}
                          {u.email && <span className="text-muted-foreground"> ({u.email})</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <label className="block text-sm">
              Package
              <select
                className="mt-1 w-full rounded-md border border-input bg-background px-2 py-2 text-sm"
                value={packageCode}
                onChange={(e) => onPackageChange(e.target.value)}
              >
                <option value="">Select a package…</option>
                {activePackages.map((p) => (
                  <option key={p.package_code} value={p.package_code}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
            {!isOneTime && (
              <label className="block text-sm">
                Billing cycle
                <select
                  className="mt-1 w-full rounded-md border border-input bg-background px-2 py-2 text-sm"
                  value={billingCycle}
                  onChange={(e) => setBillingCycle(e.target.value as 'weekly' | 'monthly')}
                >
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </label>
            )}
            <div className="grid grid-cols-2 gap-3">
              <label className="block text-sm">
                Start date
                <input
                  type="date"
                  className="mt-1 w-full rounded-md border border-input bg-background px-2 py-2 text-sm"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </label>
              <label className="block text-sm">
                End date <span className="text-muted-foreground">(optional)</span>
                <input
                  type="date"
                  className="mt-1 w-full rounded-md border border-input bg-background px-2 py-2 text-sm"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </label>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={autoRenew} onChange={(e) => setAutoRenew(e.target.checked)} />
              Auto-renew subscription
            </label>
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>

          <div className="mt-6 flex justify-end gap-2">
            <button type="button" onClick={onClose} className="rounded-md border border-input px-3 py-1.5 text-sm font-medium hover:bg-accent">
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={create.isPending}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {create.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Create
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
